import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Modal } from "@circleco/compass/components/Modal";
import type { ReactNode } from "react";
import { useLayoutEffect, useState } from "react";

const scrollFadeClass =
  "pointer-events-none absolute left-0 right-0 z-10 transition-opacity duration-200 motion-reduce:transition-none";
const customLayoutBodyClass =
  "text-primary flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-lg text-body-md";

const getModalBodyScroller = (testId: string) => {
  const root = document.querySelector(`[data-testid="${testId}"]`);
  return root?.matches(".overflow-y-auto")
    ? root
    : (root?.querySelector(".overflow-y-auto") ?? null);
};

const ScrollFade = ({
  edge,
  isStrong,
  targetTestId,
}: {
  edge: "top" | "bottom";
  isStrong?: boolean;
  targetTestId: string;
}) => {
  const [shouldShowFade, setShouldShowFade] = useState(false);

  useLayoutEffect(() => {
    const scroller = getModalBodyScroller(targetTestId);
    if (!scroller) return;

    const syncFade = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      const hasOverflow = scrollHeight > clientHeight;
      setShouldShowFade(
        edge === "top"
          ? hasOverflow && scrollTop > 1
          : hasOverflow && scrollHeight - clientHeight - scrollTop > 1,
      );
    };

    syncFade();
    scroller.addEventListener("scroll", syncFade);
    const resizeObserver = new ResizeObserver(syncFade);
    resizeObserver.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", syncFade);
      resizeObserver.disconnect();
    };
  }, [edge, targetTestId]);

  const direction = edge === "top" ? "180deg" : "0deg";
  const color = "var(--color-background-primary)";

  return (
    <div
      aria-hidden
      className={classNames(
        scrollFadeClass,
        edge === "top" ? "top-0" : "bottom-0",
        isStrong ? "h-24" : "h-10",
      )}
      style={{
        background: isStrong
          ? `linear-gradient(${direction}, ${color} 0%, ${color} 30%, transparent 100%)`
          : `linear-gradient(${direction}, ${color} 0%, transparent 72%)`,
        opacity: shouldShowFade ? 1 : 0,
      }}
    />
  );
};

interface QuickPostModalBodyProps {
  children: ReactNode;
  customLayout?: boolean;
  "data-testid": string;
  hasFloatingChrome?: boolean;
}

export const QuickPostModalBody = ({
  children,
  customLayout = false,
  "data-testid": dataTestId,
  hasFloatingChrome = false,
}: QuickPostModalBodyProps) => {
  const body = customLayout ? (
    <div className={customLayoutBodyClass} data-testid={dataTestId}>
      {children}
    </div>
  ) : (
    <Modal.Body data-testid={dataTestId}>{children}</Modal.Body>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <ScrollFade
        edge="top"
        isStrong={hasFloatingChrome}
        targetTestId={dataTestId}
      />
      {hasFloatingChrome && (
        <ScrollFade edge="bottom" isStrong targetTestId={dataTestId} />
      )}
      {body}
    </div>
  );
};
